"use client";

import Link from "next/link";
import Image from "next/image";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/ui/dropdown-menu";
import { LogOut, ShoppingCart, UserCircleIcon } from "lucide-react";

import userWhite from "@/public/shop/user.svg";
import userBlack from "@/public/shop/user-black.svg";

export default function UserMenu({
  theme,
}: {
  theme: "transparent" | "light";
}) {
  const { user, isAuthenticated, logout } = useAuth();

  if (!isAuthenticated) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="group h-full p-[2rem] hover:bg-white transition-colors">
          <Image
            src={theme === "transparent" ? userWhite : userBlack}
            alt="Benutzer"
            className={
              theme === "transparent"
                ? "w-[3rem] h-[3rem] min-w-[3rem] min-h-[3rem] group-hover:invert transition-all"
                : "w-[3rem] h-[3rem] min-w-[3rem] min-h-[3rem]"
            }
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-[20rem] gap-0 rounded-[1.5rem]"
        align="end"
      >
        <DropdownMenuLabel className="flex flex-col p-[2rem] pb-[1rem] whitespace-nowrap overflow-ellipsis">
          <span className="text-black/80 leading-[1em]">
            {user?.firstName} {user?.lastName}
          </span>
          <span className="text-[1.5rem] font-[400] text-black/50">
            {user?.email}
          </span>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuItem className="focus:!bg-transparent focus:!text-current ring-0 outline-none px-[.5rem] py-0 opacity-60 hover:opacity-100 transition-opacity">
            <Link
              href="/shop/account"
              className="w-full block px-[1rem] py-3 text-sm text-black/90 hover:bg-gray-100 rounded-[.5rem] transition-colors"
            >
              <UserCircleIcon className="w-[2rem] h-[2rem] inline-block me-[.5rem] mb-1 opacity-80" />
              Mein Konto
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem className="focus:!bg-transparent focus:!text-current ring-0 outline-none px-[.5rem] py-0 opacity-60 hover:opacity-100 transition-opacity">
            <Link
              href="/shop/account/orders"
              className="w-full block px-[1rem] py-3 text-sm text-black/90 hover:bg-gray-100 rounded-[.5rem] transition-colors"
            >
              <ShoppingCart className="w-[2rem] h-[2rem] inline-block me-[.5rem] mb-1 opacity-80" />
              Bestellverlauf
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={async () => {
              await logout();
            }}
            className="cursor-pointer w-full block px-[1.75rem] pt-3 pb-5 text-sm text-black/90 hover:!bg-gray-100 focus:!bg-transparent focus:!text-current ring-0 outline-none rounded-[.5rem] transition-all opacity-60 hover:opacity-100"
          >
            <LogOut className="w-[1.75rem] h-[2rem] inline-block me-[.75rem] mb-1 opacity-80 rotate-180" />
            Abmelden
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
